import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnChanges,
  type OnInit,
  type SimpleChanges,
} from '@angular/core';
import {
  CarouselArrowPosition,
  CarouselScrollBehaviour,
} from '@freelancer/ui/carousel';
import { IconColor } from '@freelancer/ui/icon';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, TextSize } from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'app-companies-trust-logo',
  template: `
    <div class="CompaniesTrust">
      <p
        [class]="cls"
        [class.desktop:mr-xxlarge]="true"
        [class.tablet:mr-mid]="true"
        [class.tablet:mb-none]="true"
        [class.mb-small]="true"
        i18n="As used by text"
      >
        As used by
      </p>
      <div class="CompaniesTrust-companies">
        <div
          class="CompaniesTrust-companies-list mb-small tablet:mb-none mobile:hide"
        >
          <ng-container *ngTemplateOutlet="adobeImage"></ng-container>
          <ng-container *ngTemplateOutlet="facebookImage"></ng-container>
          <ng-container *ngTemplateOutlet="deloitteImage"></ng-container>
          <ng-container *ngTemplateOutlet="nasaImage"></ng-container>
          <ng-container *ngTemplateOutlet="ibmImage"></ng-container>
          <ng-container *ngTemplateOutlet="airbusImage"></ng-container>
          @if (isRedesign) {
            <ng-container *ngTemplateOutlet="telstraImage"></ng-container>
            <ng-container *ngTemplateOutlet="fujitsuImage"></ng-container>
            <ng-container *ngTemplateOutlet="googleImage"></ng-container>
          }
        </div>
        <fl-carousel
          class="mobile:show"
          [currentIndex]="0"
          [slidesToShow]="3"
          [padding]="4"
          [arrowPosition]="CarouselArrowPosition.OUTSIDE"
          [scrollBehaviour]="CarouselScrollBehaviour.GROUP"
        >
          <fl-carousel-prev-btn>
            <fl-icon
              class="CarouselArrow"
              [color]="isDarkMode ? IconColor.LIGHT : IconColor.FOREGROUND"
              [name]="'ui-arrow-back'"
              [useIconFont]="true"
            ></fl-icon>
          </fl-carousel-prev-btn>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="adobeImage"></ng-container>
          </fl-carousel-item>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="facebookImage"></ng-container>
          </fl-carousel-item>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="deloitteImage"></ng-container>
          </fl-carousel-item>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="nasaImage"></ng-container>
          </fl-carousel-item>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="ibmImage"></ng-container>
          </fl-carousel-item>
          <fl-carousel-item>
            <ng-container *ngTemplateOutlet="airbusImage"></ng-container>
          </fl-carousel-item>
          @if (isRedesign) {
            <fl-carousel-item>
              <ng-container *ngTemplateOutlet="telstraImage"></ng-container>
            </fl-carousel-item>
            <fl-carousel-item
              ><ng-container *ngTemplateOutlet="fujitsuImage"></ng-container>
            </fl-carousel-item>
            <fl-carousel-item
              ><ng-container *ngTemplateOutlet="googleImage"></ng-container>
            </fl-carousel-item>
          }
          <fl-carousel-next-btn>
            <fl-icon
              class="CarouselArrow"
              [color]="isDarkMode ? IconColor.LIGHT : IconColor.FOREGROUND"
              [name]="'ui-arrow-forward'"
              [useIconFont]="true"
            ></fl-icon>
          </fl-carousel-next-btn>
        </fl-carousel>
      </div>
    </div>
    <ng-template #adobeImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Adobe Icon"
        i18n-alt="Adobe Icon Alternative Text"
        [class.CompaniesTrust--adobe]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/adobe-logo-darkmode.svg'
            : 'home/redesign/companies/adobe-logo.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="110"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #facebookImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Facebook Icon"
        i18n-alt="Facebook Icon Alternative Text"
        [class.CompaniesTrust--facebook]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/facebook-corporate-logo2-darkmode.svg'
            : 'home/redesign/companies/facebook-corporate-logo2.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="151"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #deloitteImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Deloitte Icon"
        i18n-alt="Deloitte Icon Alternative Text"
        [class.CompaniesTrust--deloitte]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/deloitte-logo2-darkmode.svg'
            : 'home/redesign/companies/deloitte-logo2.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="106"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #nasaImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="NASA Icon"
        i18n-alt="NASA Icon Alternative Text"
        [class.CompaniesTrust--nasa]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/nasa-logo-v4.png'
            : 'home/redesign/companies/nasa-logo-v4-lightmode.png'
        "
        [explicitHeight]="92"
        [explicitWidth]="110"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #ibmImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="IBM Icon"
        i18n-alt="IBM Icon Alternative Text"
        [class.CompaniesTrust--ibm]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/ibm-logo2-darkmode.svg'
            : 'home/redesign/companies/ibm-logo2.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="62"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #airbusImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Facebook Icon"
        i18n-alt="Facebook Icon Alternative Text"
        [class.CompaniesTrust--airbus]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/airbus-logo2-darkmode.svg'
            : 'home/redesign/companies/airbus-logo2.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="105"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #telstraImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Telstra Icon"
        i18n-alt="Telstra Icon Alternative Text"
        [class.CompaniesTrust--telstra]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/telstra-logo-darkmode-1.svg'
            : 'home/redesign/companies/telstra-logo.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="116"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #fujitsuImage>
      <app-company-logo
        class="desktop:mr-large"
        alt="Fujitsu Icon"
        i18n-alt="Fujitsu Icon Alternative Text"
        [class.CompaniesTrust--fujitsu]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/fujitsu-logo-darkmode.svg'
            : 'home/redesign/companies/fujitsu-logo.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="88"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
    <ng-template #googleImage>
      <app-company-logo
        alt="Google Icon"
        i18n-alt="Google Icon Alternative Text"
        [class.CompaniesTrust--google]="isRedesign"
        [src]="
          isDarkMode
            ? 'home/redesign/companies/google-logo-darkmode.svg'
            : 'home/redesign/companies/google-logo.svg'
        "
        [explicitHeight]="50"
        [explicitWidth]="97"
        [isRedesign]="isRedesign"
      ></app-company-logo>
    </ng-template>
  `,
  styleUrls: ['./companies-trust.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompaniesTrustComponent implements OnInit, OnChanges {
  CarouselArrowPosition = CarouselArrowPosition;
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  IconColor = IconColor;

  @Input() isDarkMode = true;
  @Input() isRedesign: boolean;

  cls: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isDarkMode || changes.isRedesign) {
      this.computeClasses();
    }
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  private computeClasses(): void {
    this.cls = textVariants({
      size: TextSize.BODY_16,
      sizeTablet: this.isRedesign ? TextSize.BODY_12 : TextSize.BODY_20,
      sizeDesktop: TextSize.BODY_20,
      color: this.isDarkMode ? FontColor.LIGHT : FontColor.DARK,
      class: 'CompaniesTrust-text',
    });
  }
}
